// src/pages/Queries.js
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaTrash, FaPhone, FaEnvelope, FaImage, FaInbox, FaClock, FaCheckCircle, FaPhoneAlt, FaBan, FaFilter } from 'react-icons/fa';

const Queries = () => {
  const [queries, setQueries] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQueries();
  }, []);

  const fetchQueries = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/queries`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setQueries(data);
    } catch (err) {
      toast.error('Failed to load queries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/queries/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status }),
      });

      if (!res.ok) throw new Error('Failed to update status');
      fetchQueries();
      toast.success('Status updated successfully');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/queries/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      fetchQueries();
      toast.success('Query deleted successfully');
    } catch (err) {
      toast.error('Failed to delete query');
    }
  };

  const filteredQueries = filter === 'all'
    ? queries
    : queries.filter(q => q.status === filter);

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending': return {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        icon: FaClock,
        label: 'Pending'
      };
      case 'contacted': return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        icon: FaPhoneAlt,
        label: 'Contacted'
      };
      case 'completed': return {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        icon: FaCheckCircle,
        label: 'Completed'
      };
      case 'cancelled': return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
        icon: FaBan,
        label: 'Cancelled'
      };
      default: return {
        bg: 'bg-slate-50',
        text: 'text-slate-700',
        border: 'border-slate-200',
        icon: FaInbox,
        label: status
      };
    }
  };

  const filterButtons = [
    { key: 'all', label: 'All', count: queries.length, color: 'primary' },
    { key: 'pending', label: 'Pending', count: queries.filter(q => q.status === 'pending').length, color: 'amber' },
    { key: 'contacted', label: 'Contacted', count: queries.filter(q => q.status === 'contacted').length, color: 'blue' },
    { key: 'completed', label: 'Completed', count: queries.filter(q => q.status === 'completed').length, color: 'emerald' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading queries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8"
      >
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Customer Queries</h1>
          <p className="text-slate-500 mt-1">
            {queries.length} total | {queries.filter(q => q.status === 'pending').length} pending
          </p>
        </div>
      </motion.div>

      {/* Filter Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-2 mb-6 overflow-x-auto pb-2"
      >
        <FaFilter className="text-slate-400 text-sm flex-shrink-0" />
        {filterButtons.map((btn) => {
          const isActive = filter === btn.key;
          return (
            <button
              key={btn.key}
              onClick={() => setFilter(btn.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                isActive
                  ? btn.key === 'all'
                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30'
                    : btn.key === 'pending'
                    ? 'bg-amber-500 text-white'
                    : btn.key === 'contacted'
                    ? 'bg-blue-500 text-white'
                    : 'bg-emerald-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {btn.label}
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                isActive ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {btn.count}
              </span>
            </button>
          );
        })}
      </motion.div>

      {/* Queries List */}
      <div className="space-y-4">
        {filteredQueries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16 bg-white rounded-xl shadow-card border border-slate-100"
          >
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaInbox className="text-2xl text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No queries found</h3>
            <p className="text-slate-500">
              {filter === 'all' ? 'No customer queries yet' : `No ${filter} queries`}
            </p>
          </motion.div>
        ) : (
          filteredQueries.map((query, index) => {
            const statusConfig = getStatusConfig(query.status);
            const StatusIcon = statusConfig.icon;

            return (
              <motion.div
                key={query._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden hover:shadow-soft transition-all duration-300"
              >
                <div className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Query Details */}
                    <div className="flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-800">{query.name}</h3>
                          <p className="text-sm text-slate-500 mt-0.5">
                            {new Date(query.createdAt).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
                          <StatusIcon className="text-[10px]" />
                          {statusConfig.label}
                        </span>
                      </div>

                      {/* Contact Info */}
                      <div className="flex flex-wrap gap-4 mb-4">
                        <a
                          href={`tel:${query.phone}`}
                          className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                        >
                          <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                            <FaPhone className="text-primary-600 text-xs" />
                          </div>
                          <span className="font-medium">{query.phone}</span>
                        </a>
                        {query.email && (
                          <a
                            href={`mailto:${query.email}`}
                            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                          >
                            <div className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center">
                              <FaEnvelope className="text-primary-600 text-xs" />
                            </div>
                            <span className="font-medium">{query.email}</span>
                          </a>
                        )}
                      </div>

                      {/* Query Details */}
                      <div className="flex flex-wrap gap-3 mb-4">
                        <span className="inline-flex items-center px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-700">
                          <span className="font-medium mr-1.5">Category:</span>
                          {query.productCategory}
                        </span>
                        {query.color && (
                          <span className="inline-flex items-center px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-700">
                            <span className="font-medium mr-1.5">Color:</span>
                            {query.color}
                          </span>
                        )}
                        {query.size && (
                          <span className="inline-flex items-center px-3 py-1.5 bg-slate-100 rounded-lg text-sm text-slate-700">
                            <span className="font-medium mr-1.5">Size:</span>
                            {query.size}
                          </span>
                        )}
                      </div>

                      {/* Details Text */}
                      {query.details && (
                        <div className="mb-4">
                          <p className="text-sm font-medium text-slate-700 mb-1.5">Details:</p>
                          <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg leading-relaxed">
                            {query.details}
                          </p>
                        </div>
                      )}

                      {/* Reference Image */}
                      {query.referenceImage && (
                        <div>
                          <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <FaImage className="text-slate-400" />
                            Reference Image
                          </p>
                          <img
                            src={`${process.env.REACT_APP_API_URL}/api/images/${query.referenceImage}`}
                            alt="Reference"
                            className="w-32 h-32 object-cover rounded-xl border border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/images/${query.referenceImage}`, '_blank')}
                          />
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row lg:flex-col gap-2 lg:min-w-[160px]">
                      <select
                        value={query.status}
                        onChange={(e) => handleStatusChange(query._id, e.target.value)}
                        className="flex-1 lg:flex-none px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all cursor-pointer"
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>

                      <button
                        onClick={() => handleDelete(query._id)}
                        className="flex-1 lg:flex-none bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 font-medium transition-colors"
                      >
                        <FaTrash className="text-sm" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Queries;
